import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { branch, compose, withProps } from 'recompose';
import get from 'lodash/get';
import once from 'lodash/once';

import { selectors as i18nSelectors } from 'mage-i18n';
import { cookieHelper } from 'mage-common';
import { selectors as coreSelectors } from 'mage-core';
import { selectors as configSelectors } from 'mage-config';
import { isRunningContextCypress } from 'moet-test-utils/cypress/utils';
import { analytics, GoogleAnalyticsHelmet } from 'moet-core';

import { IS_ONBOARDING_APP, FEATURES } from '@/constants';
import { getExperimentsAsUserAttributes } from '@/selectors/experiments';
import { getKrnFromWidgetToken } from '@/selectors/bpc';
import { getWidgetTypeDefaultUnmanaged } from '@/selectors/widget';
import { getMerchant, getMerchantCountryKey, getUserMerchantId } from '@/selectors/merchant';
import { getFeatureByKey } from '@/selectors/featuresConfig';

const MPARTICLE_API_KEY = 'us1-2c1103a7e46a7340ae6174fea22528f2';

const loginOnce = once(analytics.login);
const logoutOnce = once(analytics.logout);

/**
 * Hook that returns a value from the previous render. As an alternative for `componentDidUpdate`
 * which is only available in class components.
 * @param {any} value Variable to which the previous value should be provided
 * @returns the value from the previous component render
 */
const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  return ref.current;
};

/**
 * Creates a higher-order component that calls a given function a single time once the performance
 * cookies are allowed by the user.
 * @param {Function} callback Function being called once when the user accepts cookies.
 * @returns HOC that adds the cookie consent logic
 */
export const withCookieConsentCallback = (callback = () => {}) => Component => {
  const CookieConsentCallback = props => {
    const cookieConsentGroups = cookieHelper.getConsentGroups();

    // Store the cookie acceptance status as state variable
    const [{ C0002: acceptedPerformanceCookies = false }, setCookieConsentGroups] = useState(
      cookieConsentGroups,
    );

    // Keep the value from the previous component render
    const acceptedPerformanceCookiesPrevious = usePrevious(acceptedPerformanceCookies);

    useEffect(() => {
      const optanonWrapper = window.OptanonWrapper || (() => null);
      window.OptanonWrapper = () => {
        optanonWrapper();
        setCookieConsentGroups(cookieHelper.getConsentGroups());
      };
      return () => {
        // Cleanup the useEffect by resetting OptanonWrapper to original function
        window.OptanonWrapper = optanonWrapper;
      };
    }, []);

    // Only when the cookie acceptance status changes from false to true, run the callback once
    if (!acceptedPerformanceCookiesPrevious && acceptedPerformanceCookies) {
      callback();
    }

    return <Component {...props} acceptedPerformanceCookies={acceptedPerformanceCookies} />;
  };

  return CookieConsentCallback;
};

const AnalyticsAndCookieSetupComponent = ({
  acceptedPerformanceCookies = false,
  country,
  customerid,
  env,
  experimentsAsUserAttributes,
  shouldForceAnalyticsEventsLogging,
  isEnvironmentE2ETest,
  isOneTrustEnabled,
  language,
  merchant,
  realm,
  title,
  widgetType,
}) => {
  const isDevelopmentMode = !shouldForceAnalyticsEventsLogging && env !== 'production';
  const areCookiesAccepted =
    shouldForceAnalyticsEventsLogging || !isOneTrustEnabled || acceptedPerformanceCookies;
  const identifyRequest = IS_ONBOARDING_APP ? null : { userIdentities: { customerid } };

  analytics.useAnalyticsWith(analytics, { areCookiesAccepted, isEnvironmentE2ETest });

  // When opened in a E2E test we skip the analytics setup completely.
  if (isEnvironmentE2ETest) {
    return null;
  }

  // IDSync callback - a callback function to run on completion of the initial identify request
  // which is done by the mParticle SDK.
  const identityCallback = result => {
    const user = result.getUser();
    if (user) {
      const { userIdentities } = user.getUserIdentities();

      // If the user is currently loading the Signup flow and there is still an active mParticle
      // session with a MID (customerid), we first logout that session and call the identityCallback
      // again as a callback.
      if (IS_ONBOARDING_APP && !!userIdentities.customerid) {
        return logoutOnce(identityCallback);
      }

      // If the MID differs from the one that the mParticle SDK has stored in the session, it means
      // that we have a new user session. So we switch to a new mParticle session with that new MID
      // and call the identityCallback again as a callback.
      if (!!customerid && userIdentities.customerid !== customerid) {
        return loginOnce({ customerid, callback: identityCallback });
      }

      user.setUserAttribute('widgetType', widgetType);
      user.setUserAttribute('environment', env);
      if (experimentsAsUserAttributes && Object.keys(experimentsAsUserAttributes).length) {
        user.setUserAttributes(experimentsAsUserAttributes);
      }

      // If the toggle is ON we record the page view event directly. Otherwise it is only being
      // recorded once the cookies are accepted. For that we use the `withCookieConsentCallback`.
      if (shouldForceAnalyticsEventsLogging) {
        analytics.logPageView();
      }
    }
  };

  analytics.useMParticleTracking(MPARTICLE_API_KEY, {
    areCookiesAccepted,
    isDevelopmentMode,
    isEnvironmentE2ETest,
    identifyRequest,
    identityCallback,
  });

  return (
    <GoogleAnalyticsHelmet
      areCookiesAccepted={areCookiesAccepted}
      country={country}
      email={get(merchant, 'email', '')}
      env={env}
      language={language}
      realm={realm}
      title={title}
    />
  );
};

AnalyticsAndCookieSetupComponent.propTypes = {
  country: PropTypes.string.isRequired,
  env: PropTypes.string.isRequired,
  isEnvironmentE2ETest: PropTypes.bool.isRequired,
  isOneTrustEnabled: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  merchant: PropTypes.object.isRequired,
  realm: PropTypes.string.isRequired,
  shouldForceAnalyticsEventsLogging: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  acceptedPerformanceCookies: PropTypes.bool,
  customerid: IS_ONBOARDING_APP ? PropTypes.string : PropTypes.string.isRequired,
  experimentsAsUserAttributes: PropTypes.object,
  widgetType: PropTypes.string,
};

export const AnalyticsAndCookieSetup = compose(
  connect(state => ({
    isOneTrustEnabled: configSelectors.isOneTrustEnabled(state),
    country: getMerchantCountryKey(state) || '',
    env: configSelectors.environment(state),
    experimentsAsUserAttributes: getExperimentsAsUserAttributes(state),
    mid: getUserMerchantId(state),
    contractKrn: getKrnFromWidgetToken(state),
    merchant: getMerchant(state),
    language: i18nSelectors.language(state),
    realm: coreSelectors.keycloakRealm(state),
    title: coreSelectors.title(state),
    widgetType: getWidgetTypeDefaultUnmanaged(state),
    isEnvironmentE2ETest: isRunningContextCypress(window),
    shouldForceAnalyticsEventsLogging: getFeatureByKey(FEATURES.FORCE_ANALYTICS_EVENTS_LOGGING)(
      state,
    ),
  })),
  withProps(({ mid, contractKrn }) => ({ customerid: contractKrn || mid || null })),
  branch(
    // Execute a single page view after the performance cookie has been consented by the user. If
    // the feature toggle is ON we record the page view event in the `identityCallback` function
    // instead.
    ({ isEnvironmentE2ETest, shouldForceAnalyticsEventsLogging }) =>
      !shouldForceAnalyticsEventsLogging && !isEnvironmentE2ETest,
    withCookieConsentCallback(analytics.logPageView),
  ),
)(AnalyticsAndCookieSetupComponent);
